import { isIncidentEditable } from '@collector/shared-ui-domain'
import { Incident } from '@collector/sportsapi-client'
import {
  Confirmation,
  EventStatusId,
  IncidentId,
  SportIncident,
  YesNoEnum,
} from '@collector/sportsapi-client-legacy'
import {
  HistoryIncidentsConfig,
  IncidentSettings,
  isSubParticipantSetting,
  SubParticipantType,
  VIPIncidentsConfig,
} from '@desktop/views/Relation/Sports/configuration'
import { IncidentPlayerOptionValue } from '@desktop/views/Relation/types'

export function isIncidentImportant(
  incident: Incident,
  incidentIdToSportIncidentMap: Record<IncidentId, SportIncident>,
): boolean {
  return (
    incidentIdToSportIncidentMap[incident.incident_id]?.important ===
    YesNoEnum.Yes
  )
}

export function isIncidentEmpty(
  incident: Incident,
  historyIncidentsConfig: HistoryIncidentsConfig,
): boolean {
  if (!isIncidentEditable(incident)) {
    return false
  }

  const incidentSettings = historyIncidentsConfig[incident.incident_id]

  if (incidentSettings) {
    const hasAllRequiredSubParticipants =
      hasRequiredSubParticipant(incident, incidentSettings, 'participant') &&
      hasRequiredSubParticipant(incident, incidentSettings, 'assistant') &&
      hasRequiredSubParticipant(
        incident,
        incidentSettings,
        'secondary_assistant',
      )

    return !hasAllRequiredSubParticipants
  }

  return false
}

/**
 * Determines whether the given `incident` is classified as VIP.
 *
 * If `isIncidentEditable(incident)` returns `false`, the function returns `false` and the `incident` is not considered VIP.
 * If `vipIncidentsConfig` is `null`, all editable incidents are considered VIP by default.
 * If `vipIncidentsConfig` is provided, the function returns `true` only if the `incident.incident_id` exists in the `vipIncidentsConfig.incidents` set.
 */
export function isVIPIncident(
  incident: Incident,
  vipIncidentsConfig: VIPIncidentsConfig | null,
): boolean {
  if (!isIncidentEditable(incident)) {
    return false
  }

  if (!vipIncidentsConfig) {
    return true
  }

  return vipIncidentsConfig.incidents.has(incident.incident_id)
}

function hasRequiredSubParticipant(
  incident: Incident,
  settings: IncidentSettings,
  key: SubParticipantType,
): boolean {
  const setting = settings[key]

  if (isSubParticipantSetting(setting)) {
    if (setting.required) {
      if (key === 'secondary_assistant') {
        return (
          !!incident.additional_info.secondary_assistant_id ||
          !!incident.additional_info.secondary_assistant_name
        )
      }

      return incident[`${key}_id`] !== null || incident[`${key}_name`] !== null
    }

    return true
  }

  return true
}

export function hasIncidentParticipantId(
  incident: Incident,
  participantId: Incident['participant_team_id'],
): boolean {
  return incident.participant_team_id === participantId
}

export function hasIncidentConfirmation(
  incident: Incident,
  confirmation: Confirmation,
): boolean {
  return incident.confirmation === confirmation
}

export function hasIncidentEventStatus(
  incident: Incident,
  eventStatuses: Set<EventStatusId>,
): boolean {
  return eventStatuses.has(incident.status_id)
}

export function hasIncidentId(
  incident: Incident,
  incidentIds: Set<IncidentId>,
): boolean {
  return incidentIds.has(incident.incident_id)
}

export function hasIncidentParticipantOrAssistant(
  incident: Incident,
  playerOptions: Set<IncidentPlayerOptionValue>,
): boolean {
  for (const player of playerOptions) {
    if (
      (Number(player.teamId) === incident.participant_team_id &&
        incident.participant_name === player.shortName) ||
      (incident.assistant_name !== null &&
        incident.assistant_name === player.shortName)
    ) {
      return true
    }
  }

  return false
}

/**
 * Checks if the `incident` has at least one of the specified attributes by name.
 * This function dynamically matches attribute names from the API data.
 *
 * @param incident - The incident to check
 * @param attributeNames - Set of attribute display names selected in the filter
 * @param incidentAttributeIdToAttributeMap - Map from attribute ID to attribute details (from sport API)
 * @returns `true` if the incident contains at least one attribute with a matching name
 */
export function hasIncidentAttribute(
  incident: Incident,
  attributeNames: Set<string>,
  incidentAttributeIdToAttributeMap: Record<
    number,
    { id: number; name: string }
  >,
): boolean {
  if (!incident.attribute_ids || incident.attribute_ids.length === 0) {
    return false
  }

  // Check if any of the incident's attributes match the selected attribute names
  for (const attributeId of incident.attribute_ids) {
    const attribute = incidentAttributeIdToAttributeMap[attributeId]
    if (attribute) {
      const attributeName = attribute.name
      if (
        attributeNames.has(attributeName) ||
        attributeNames.has(attributeName.toLowerCase())
      ) {
        return true
      }
    }
  }

  return false
}
